import { StateCreator } from 'zustand';
import type { AppState } from '../../store/app-state';
import { logger } from '../../utils/logger.ts';
import { LightingCue, FollowSpotCue, FollowSpot } from './types';
import { deleteFollowSpot } from './lighting-api.ts';
import type { ProjectScope } from '../sync/project-scope.ts';
import {
    markPendingFollowSpotCueWrite,
    markPendingFollowSpotDelete,
    markPendingFollowSpotWrite,
    markPendingLightingCueWrite,
} from '../sync/lighting-pending-writes';

export interface LightingSlice {
    lightingCues: LightingCue[];
    followSpotCues: FollowSpotCue[];
    followSpots: FollowSpot[];
    lightingActions: {
        // Lighting Cues
        addLightingCue: (cue: Partial<LightingCue>) => string;
        updateLightingCue: (id: string, updates: Partial<LightingCue>) => void;
        deleteLightingCue: (id: string) => void;
        reorderLightingCues: (sceneId: string, oldIndex: number, newIndex: number) => void;

        // Follow Spot Cues
        addFollowSpotCue: (cue: Partial<FollowSpotCue>) => string;
        updateFollowSpotCue: (id: string, updates: Partial<FollowSpotCue>) => void;
        deleteFollowSpotCue: (id: string) => void;
        reorderFollowSpotCues: (sceneId: string, oldIndex: number, newIndex: number) => void;

        // Follow Spots (config)
        addFollowSpot: (spot: Partial<FollowSpot>) => string;
        updateFollowSpot: (id: string, updates: Partial<FollowSpot>) => void;
        deleteFollowSpot: (id: string) => void;

        // Project scoping
        clearLighting: () => void;
    };
}

function getLightingProjectScope(state: AppState): ProjectScope | null {
    if (!state.cloud.enabled || !state.cloud.orgId || !state.cloud.projectId) {
        return null;
    }
    return { orgId: state.cloud.orgId, projectId: state.cloud.projectId };
}

export const createLightingSlice: StateCreator<AppState, [['zustand/immer', never]], [], LightingSlice> = (set, get) => ({
    lightingCues: [],
    followSpotCues: [],
    followSpots: [],
    lightingActions: {
        // ── Lighting Cues ──────────────────────────────────────────
        addLightingCue: (cue) => {
            const id = crypto.randomUUID();
            set(state => {
                state.lightingCues.push({
                    id,
                    sceneId: cue.sceneId || '',
                    cueNumber: cue.cueNumber || '',
                    cueLine: cue.cueLine || '',
                    description: cue.description || '',
                    notes: cue.notes || '',
                    sortOrder: cue.sortOrder ?? state.lightingCues.length,
                    ...cue,
                });
            });
            const scope = getLightingProjectScope(get());
            if (scope) {
                markPendingLightingCueWrite(scope, id);
            }
            return id;
        },
        updateLightingCue: (id, updates) => {
            const scope = getLightingProjectScope(get());
            let didUpdate = false;
            set(state => {
                const idx = state.lightingCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    Object.assign(state.lightingCues[idx]!, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingLightingCueWrite(scope, id);
            }
        },
        deleteLightingCue: (id) => {
            const scope = getLightingProjectScope(get());
            let didDelete = false;
            set(state => {
                const idx = state.lightingCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    state.lightingCues[idx]!.deleted = true;
                    state.lightingCues[idx]!.deletedAt = Date.now();
                    didDelete = true;
                }
            });
            if (scope && didDelete) {
                markPendingLightingCueWrite(scope, id);
            }
        },
        reorderLightingCues: (sceneId, oldIndex, newIndex) => {
            const scope = getLightingProjectScope(get());
            const reorderedCueIds: string[] = [];
            set(state => {
                const sceneCues = state.lightingCues
                    .filter(c => !c.deleted && c.sceneId === sceneId)
                    .toSorted((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                const [moved] = sceneCues.splice(oldIndex, 1);
                sceneCues.splice(newIndex, 0, moved!);
                for (const [i, c] of sceneCues.entries()) {
                    const storeIdx = state.lightingCues.findIndex(s => s.id === c.id);
                    if (storeIdx !== -1) {
                        state.lightingCues[storeIdx]!.sortOrder = i;
                        reorderedCueIds.push(c.id);
                    }
                }
            });
            if (scope) {
                for (const id of reorderedCueIds) {
                    markPendingLightingCueWrite(scope, id);
                }
            }
        },

        // ── Follow Spot Cues ───────────────────────────────────────
        addFollowSpotCue: (cue) => {
            const id = crypto.randomUUID();
            set(state => {
                state.followSpotCues.push({
                    id,
                    spotId: cue.spotId || '',
                    cueNumber: cue.cueNumber || '',
                    characterId: cue.characterId || '',
                    action: cue.action || 'pickup',
                    frame: cue.frame || '',
                    size: cue.size || 'full',
                    intensity: cue.intensity,
                    linkedLxCueId: cue.linkedLxCueId,
                    sceneId: cue.sceneId || '',
                    notes: cue.notes || '',
                    sortOrder: cue.sortOrder ?? state.followSpotCues.length,
                    ...cue,
                });
            });
            const scope = getLightingProjectScope(get());
            if (scope) {
                markPendingFollowSpotCueWrite(scope, id);
            }
            return id;
        },
        updateFollowSpotCue: (id, updates) => {
            const scope = getLightingProjectScope(get());
            let didUpdate = false;
            set(state => {
                const idx = state.followSpotCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    Object.assign(state.followSpotCues[idx]!, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingFollowSpotCueWrite(scope, id);
            }
        },
        deleteFollowSpotCue: (id) => {
            const scope = getLightingProjectScope(get());
            let didDelete = false;
            set(state => {
                const idx = state.followSpotCues.findIndex(c => c.id === id);
                if (idx !== -1) {
                    state.followSpotCues[idx]!.deleted = true;
                    state.followSpotCues[idx]!.deletedAt = Date.now();
                    didDelete = true;
                }
            });
            if (scope && didDelete) {
                markPendingFollowSpotCueWrite(scope, id);
            }
        },
        reorderFollowSpotCues: (sceneId, oldIndex, newIndex) => {
            const scope = getLightingProjectScope(get());
            const reorderedCueIds: string[] = [];
            set(state => {
                const sceneCues = state.followSpotCues
                    .filter(c => !c.deleted && c.sceneId === sceneId)
                    .toSorted((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
                const [moved] = sceneCues.splice(oldIndex, 1);
                sceneCues.splice(newIndex, 0, moved!);
                for (const [i, c] of sceneCues.entries()) {
                    const storeIdx = state.followSpotCues.findIndex(s => s.id === c.id);
                    if (storeIdx !== -1) {
                        state.followSpotCues[storeIdx]!.sortOrder = i;
                        reorderedCueIds.push(c.id);
                    }
                }
            });
            if (scope) {
                for (const id of reorderedCueIds) {
                    markPendingFollowSpotCueWrite(scope, id);
                }
            }
        },

        // ── Follow Spots (config) ─────────────────────────────────
        addFollowSpot: (spot) => {
            const id = crypto.randomUUID();
            set(state => {
                state.followSpots.push({
                    id,
                    label: spot.label || `Spot ${state.followSpots.length + 1}`,
                    operatorName: spot.operatorName || '',
                    position: spot.position || '',
                });
            });
            const scope = getLightingProjectScope(get());
            if (scope) {
                markPendingFollowSpotWrite(scope, id);
            }
            return id;
        },
        updateFollowSpot: (id, updates) => {
            const scope = getLightingProjectScope(get());
            let didUpdate = false;
            set(state => {
                const idx = state.followSpots.findIndex(s => s.id === id);
                if (idx !== -1) {
                    Object.assign(state.followSpots[idx]!, updates);
                    didUpdate = true;
                }
            });
            if (scope && didUpdate) {
                markPendingFollowSpotWrite(scope, id);
            }
        },
        deleteFollowSpot: (id) => {
            const scope = getLightingProjectScope(get());
            const didDelete = get().followSpots.some(spot => spot.id === id);
            set(state => {
                state.followSpots = state.followSpots.filter(s => s.id !== id);
            });
            if (scope && didDelete) {
                markPendingFollowSpotDelete(scope, id);
                void deleteFollowSpot(scope, id).catch(error => {
                    logger.error('[lighting-store] Failed to preserve follow spot deletion:', error);
                });
            }
        },

        // ── Clear ──────────────────────────────────────────────────
        clearLighting: () => {
            set(state => {
                state.lightingCues = [];
                state.followSpotCues = [];
                state.followSpots = [];
            });
        },
    },
});
